import React, { useState, useEffect } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Button, Spin, Alert } from 'antd';
import { DownloadOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';

const PDFPreviewModal = ({ 
  isOpen, 
  onClose, 
  pdfBlob, 
  title = "PDF Preview",
  onDownload,
  isLoading = false,
  error = null
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    let currentUrl = null;
    
    if (pdfBlob && isOpen) {
      setIsLoadingPreview(true);
      try {
        currentUrl = URL.createObjectURL(pdfBlob);
        setPdfUrl(currentUrl);
      } catch (err) {
        console.error('Error creating PDF URL:', err);
      } finally {
        setIsLoadingPreview(false);
      }
    }

    // Cleanup URL when modal closes or component unmounts
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      if (!isOpen) {
        setPdfUrl(null);
      }
    };
  }, [pdfBlob, isOpen]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (pdfBlob) {
      // Default download behavior
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statement_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const modalFooter = [
    <Button 
      key="close" 
      onClick={onClose}
      icon={<CloseOutlined />}
    >
      Close
    </Button>,
    <Button 
      key="download" 
      type="primary" 
      onClick={handleDownload}
      icon={<DownloadOutlined />}
      disabled={!pdfBlob || isLoading}
    >
      Download PDF
    </Button>
  ];

  return (
    <ResponsiveModal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EyeOutlined />
          {title}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={modalFooter}
      width="90vw"
      style={{ top: 20 }}
      bodyStyle={{ 
        padding: 0, 
        height: '80vh', 
        display: 'flex', 
        flexDirection: 'column' 
      }}
      destroyOnClose={true}
    >
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0
      }}>
        {isLoading && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1,
            minHeight: '200px'
          }}>
            <Spin size="large" tip="Generating PDF preview..." />
          </div>
        )}

        {error && (
          <div style={{ padding: '16px' }}>
            <Alert
              message="Error Loading PDF"
              description={error}
              type="error"
              showIcon
            />
          </div>
        )}

        {!isLoading && !error && pdfUrl && (
          <div style={{ flex: 1, minHeight: 0 }}>
            {isLoadingPreview ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px'
              }}>
                <Spin tip="Loading preview..." />
              </div>
            ) : (
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '4px'
                }}
                title="PDF Preview"
              />
            )}
          </div>
        )}

        {!isLoading && !error && !pdfBlob && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1,
            color: '#999'
          }}>
            No PDF content available
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

export default PDFPreviewModal;
